import { doc, collection, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/firebase'
import { canManageMeetings } from '@/utils/meetingPermissions'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Live probe of the first-slice meeting gate (Developer/vision/MEETINGS_CALENDAR.md):
// reconcileMeetings(studentId) requires edit rights (CM of record / 504
// coordinator), not mere read. A forged organizerEmail is always denied.
//
// Permission is checked even when calendar sync is off — a skip is only a
// valid success after the caller is allowed.

const FOREIGN_EMAIL = 'smoke-not-me@example.invalid'

function isFunctionsDenied(err) {
  const code = err?.code || ''
  return (
    code === 'functions/permission-denied' ||
    code === 'permission-denied' ||
    code === 'functions/unauthenticated'
  )
}

async function callReconcile(data) {
  const fn = httpsCallable(functions, 'reconcileMeetings')
  try {
    const res = await fn(data)
    return { allowed: true, data: res.data || {} }
  } catch (err) {
    if (isFunctionsDenied(err)) return { allowed: false, code: err.code, message: err.message }
    throw err
  }
}

function studentPayload(ctx, { caseManagerId, plan }) {
  return {
    app: {
      studentData: {
        firstName: 'SMOKE',
        lastName: `Meet ${ctx.runId.slice(-8)}`,
        plan,
        grade: '9',
        ssid: `SMOKE-MEET-${ctx.runId}-${caseManagerId === ctx.user.uid ? 'own' : 'other'}`,
        caseManagerId
      },
      accommodations: { instruction: '', assessment: '' },
      schedule: { classServices: [] },
      staffIds: []
    },
    __smokeTest: true,
    __smokeRunId: ctx.runId,
    createdAt: serverTimestamp(),
    ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
  }
}

export default {
  id: 'meetingReconcileGate',
  label: 'Meeting reconcile gate (edit, not read)',
  description:
    'reconcileMeetings denies a forged organizer and a student you cannot manage; allows one you can.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const expectCreate = RULE_EXPECTATIONS.studentCreate(role)
    const ownRef = doc(collection(db, 'students'))
    const otherRef = doc(collection(db, 'students'))
    let ownCreated = false
    let otherCreated = false

    await step('forged organizerEmail (expected: denied)', async () => {
      const { allowed } = await callReconcile({ organizerEmail: FOREIGN_EMAIL })
      return accessDetail(false, allowed)
    })

    await step('own organizerEmail (expected: allowed or skipped)', async () => {
      const email = (ctx.user.email || '').trim()
      if (!email) throw new Error('Signed-in user has no email — cannot probe organizer path')
      const { allowed, data } = await callReconcile({ organizerEmail: email })
      if (!allowed) {
        throw new Error(
          `Denied on own organizerEmail (${data?.reason || 'no reason'}) — must be your own email`
        )
      }
      return data?.skipped
        ? `allowed, then skipped (${data.reason || 'calendar sync not enabled'})`
        : `allowed — checked=${data.checked ?? '?'}`
    })

    try {
      await step(`create own-CM student (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(ownRef, studentPayload(ctx, { caseManagerId: ctx.user.uid, plan: 'IEP' }))
        )
        ownCreated = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — students/${ownRef.id}`
          : accessDetail(expectCreate, allowed)
      })

      if (!ownCreated) {
        step.skip('reconcile own student', 'Role cannot create — later steps skipped')
      } else {
        const ownStudent = { app: { studentData: { caseManagerId: ctx.user.uid, plan: 'IEP' } } }
        const expectOwn = canManageMeetings({ uid: ctx.user.uid, role }, ownStudent)
        await step(
          `reconcile own student (expected: ${expectOwn ? 'allowed' : 'denied'})`,
          async () => {
            const { allowed, data } = await callReconcile({ studentId: ownRef.id })
            if (expectOwn && allowed && data?.skipped) {
              return `allowed, then skipped (${data.reason || 'calendar sync not enabled'})`
            }
            return accessDetail(expectOwn, allowed)
          }
        )
      }

      await step(
        `create other-CM IEP student (expected: ${fmtExpected(expectCreate)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(otherRef, studentPayload(ctx, { caseManagerId: 'smoke-other-cm', plan: 'IEP' }))
          )
          otherCreated = allowed
          return allowed
            ? `${accessDetail(expectCreate, allowed)} — students/${otherRef.id}`
            : accessDetail(expectCreate, allowed)
        }
      )

      if (!otherCreated) {
        step.skip('reconcile other-CM student', 'Role cannot create — later steps skipped')
      } else {
        const otherStudent = {
          app: { studentData: { caseManagerId: 'smoke-other-cm', plan: 'IEP' } }
        }
        const expectOther = canManageMeetings({ uid: ctx.user.uid, role }, otherStudent)
        await step(
          `reconcile other-CM student (expected: ${expectOther ? 'allowed' : 'denied'})`,
          async () => {
            const { allowed, data } = await callReconcile({ studentId: otherRef.id })
            if (expectOther && allowed && data?.skipped) {
              return `allowed, then skipped (${data.reason || 'calendar sync not enabled'})`
            }
            return accessDetail(expectOther, allowed)
          }
        )
      }
    } finally {
      await deleteDoc(ownRef).catch(() => {})
      await deleteDoc(otherRef).catch(() => {})
    }
  }
}
